import {Typography} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import {HandleMessage} from 'presentation/components/HandleMessage';
import {AuthorizationsEnum, ProfileEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {buildAlertMessageProps} from 'presentation/pages/private/booking/utils/alertMessage/buildAlertMessageProps';
import {translations} from 'presentation/translations-new';
import {emailHandler} from 'presentation/utils/emailHandler';
import {phoneHandler} from 'presentation/utils/phoneHandler';
import React, {Fragment, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useIsComingFromInitialFlowStore} from 'presentation/pages/others/selection-flow/store/useIsComingFromInitialFlowStore';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {Alert} from './Alert';
import {BeneficiaryInfo} from './BeneficiaryInfo';
import {ChangeBeneficiaryCard} from './ChangeBeneficiaryCard';
import {ConfirmationDrawerProps} from './types';

const {title, subtitle} =
	translations['pt-br'].privateFlow.requestAuthorizations.request.confirmationDrawer;
const {confirmAndContinue, updateInfo} = translations['pt-br'].commons;

function ConfirmationDrawer({
	opened,
	handleClose,
	customHandleConfirm,
	isLoading,
}: ConfirmationDrawerProps) {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {currentPhone, currentEmail} = usePersonalData();
	const phone = phoneHandler(currentPhone);
	const email = emailHandler(currentEmail);
	const [openAlertState, setOpenAlertState] = useState(false);

	const setIsComingFromInitialFlow = useIsComingFromInitialFlowStore(
		currentState => currentState.setIsComingFromInitialFlow,
	);

	const hasMissingInfo = !phone.hasValue || !email.hasValue;
	const hasInvalidInfo = phone.invalidValue || email.invalidValue;
	const shouldOpenAlert = hasMissingInfo || hasInvalidInfo;

	const handleCloseAlert = () => {
		setOpenAlertState(false);
	};

	const handleUpdateInfo = () => {
		navigate(ProfileEnum.PROFILE, {
			state: {pathname},
		});
	};

	const handleConfirmAndContinue = () => {
		if (shouldOpenAlert) {
			setOpenAlertState(true);
			return;
		}

		if (customHandleConfirm) {
			customHandleConfirm();
			return;
		}

		setIsComingFromInitialFlow(true);
		navigate(AuthorizationsEnum.REQUEST);
	};

	return (
		<Fragment>
			<Drawer.Root
				open={opened}
				onClose={handleClose}
				onOpen={handleClose}
				width="600px"
				customMaxHeight="100%"
				sx={{zIndex: 9000}}>
				<Drawer.Header onClose={handleClose} title={title} />
				<Drawer.Content>
					<Typography variant="body1" sx={{mt: 2, mb: 3}}>
						{subtitle}
					</Typography>
					<ChangeBeneficiaryCard />
					<BeneficiaryInfo
						cellPhone={phone.toString()}
						eMail={email.toString()}
						hasCellPhoneWarn={!phone.hasValue || phone.invalidValue}
						hasEMailWarn={!email.hasValue || email.invalidValue}
					/>
				</Drawer.Content>
				<Drawer.Footer
					firstButtonDataTagId={AutoSolEnum.CLICK_BOTAO_ALTERAR_DADOS_AUTOSOL}
					secondButtonDataTagId={AutoSolEnum.CLICK_CONFIRMAR_E_CONTINUAR_AUTOSOL}
					secondButtonTitle={confirmAndContinue}
					secondButtonIsLoading={isLoading}
					secondButtonDisabled={isLoading}
					secondButtonOnClick={handleConfirmAndContinue}
					firstButtonTitle={updateInfo}
					firstButtonOnClick={handleUpdateInfo}
					canStacks
				/>
			</Drawer.Root>
			<Alert
				message={
					<HandleMessage
						{...buildAlertMessageProps({
							hasPhoneValue: phone.hasValue,
							hasEmailValue: email.hasValue,
							isInvalidPhone: phone.invalidValue,
							isInvalidEmail: email.invalidValue,
						})}
					/>
				}
				open={openAlertState}
				handleClose={handleCloseAlert}
			/>
		</Fragment>
	);
}

export {ConfirmationDrawer};
